import React, { memo, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Icons,
  Avatar,
} from '@ublend-npm/aulaui-core';
import useIsClamped from '@app/hooks/useIsClamped';

import { getStarredSpaceColors } from '../../colors';
import SpaceDuration from './SpaceDuration';
import TruncatedText from './TruncatedText';

export interface Space {
  id: string;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  lastNavigation?: string | null;
  avatar?: string | null;
  isAssigned?: boolean;
  archived?: boolean;
}

interface SpaceItemProps {
  space: Space;
  isStarred: boolean;
  onStarToggle: (id: string) => void;
  onClick?: (id: string) => void;
  isStaffAccess?: boolean;
  isStaff?: boolean;
  archived?: boolean;
  isAssigned?: boolean;
}

const SpaceItem = ({
  space,
  isStarred,
  onStarToggle,
  onClick,
  isStaffAccess,
  isStaff,
  archived,
  isAssigned = true,
}: SpaceItemProps) => {
  const { palette } = useTheme();
  const colors = useMemo(() => getStarredSpaceColors(palette), [palette]);
  const [titleRef, isTitleClamped] = useIsClamped<HTMLSpanElement>(space.name);

  const noLongerAccess = !isStaff && !isAssigned;
  const showStarOption = typeof isStarred === 'boolean' && !isStaffAccess;
  const isClickable = Boolean(onClick && !noLongerAccess);

  const handleStarToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onStarToggle(space.id);
    },
    [onStarToggle, space.id]
  );

  const handleClick = useCallback(() => {
    if (isClickable && onClick) {
      onClick(space.id);
    }
  }, [isClickable, onClick, space.id]);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p={1.5}
      borderRadius={2}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleClick();
        }
      }}
      sx={{
        position: 'relative',
        minWidth: 0,
        width: '100%',
        cursor: isClickable ? 'pointer' : 'default',
        '&:hover': isClickable
          ? {
              bgcolor: 'action.hover',
            }
          : {},
        '&:hover .overlay': {
          display: 'flex',
        },
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        gap={2}
        flex={1}
        sx={{ minWidth: 0 }}
      >
        <Avatar
          kind="circular"
          src={space.avatar || undefined}
          fallbackText={space.name?.charAt(0).toUpperCase() || '?'}
          sx={{
            width: 40,
            height: 40,
            fontSize: '1rem',
            flexShrink: 0,
          }}
        />

        <Box
          flex={1}
          sx={{
            minWidth: 0,
            pr: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: 40,
          }}
        >
          <TruncatedText
            text={space.name}
            isTruncated={isTitleClamped}
            setRef={titleRef as any}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
            sxText={{
              variant: 'body1',
              color: 'text.primary',
              fontWeight: 550,
            }}
          />

          {(space.startDate || space.endDate) && (
            <Typography variant="body2" color={colors.textSecondary} noWrap>
              <SpaceDuration
                startDate={space.startDate}
                endDate={space.endDate}
                gap={8}
              />
            </Typography>
          )}
        </Box>
      </Box>

      {showStarOption && (
        <IconButton
          onClick={handleStarToggle}
          aria-label={`${
            isStarred ? 'Remove from starred spaces' : 'Add to starred spaces'
          }: ${space.name}`}
          aria-pressed={isStarred}
          size="small"
          sx={{
            color: colors.starIcon,
            flexShrink: 0,
          }}
        >
          {isStarred ? (
            <Icons.Star sx={{ width: 20, height: 19 }} />
          ) : (
            <Icons.StarBorder sx={{ width: 20, height: 19 }} />
          )}
        </IconButton>
      )}

      {noLongerAccess && (
        <Box
          className="overlay"
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            backgroundColor:
              palette.mode === 'dark'
                ? 'rgba(0, 0, 0, 0.85)'
                : 'rgba(255, 255, 255, 0.95)',
            color: palette.mode === 'dark' ? '#fff' : '#000',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            borderRadius: '8px',
            opacity: 0.95,
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '0.875rem',
            fontWeight: '500',
            textAlign: 'center',
            cursor: 'not-allowed',
            zIndex: 10,
            boxShadow:
              palette.mode === 'dark'
                ? '0 4px 8px rgba(0, 0, 0, 0.3)'
                : '0 4px 8px rgba(0, 0, 0, 0.1)',
          }}
          tabIndex={0}
        >
          You no longer have access to this space.
        </Box>
      )}
    </Box>
  );
};

export default memo(SpaceItem);
